import {
  buildStyles,
  CircularProgressbarWithChildren,
} from "react-circular-progressbar";
import RecursiveProgressbar from "./common/RecursiveProgressbar";
import { CARD_COLOR, CARD_ROTATION } from "./common/Agenda";
import { formatTime } from "../helper/timeCalculations";
import { CARD_TYPES } from "../helper/cardTypes";
import LiveControls from "./common/LiveControls";
import useWakeLock from "../helper/useWakeLock";
import useTimerData from "../helper/useTimerData";
import useTimerSound from "../helper/useTimerSound";
import useSoundSettings from "../helper/useSoundSettings";
import { IconButton } from "@mui/material";
import { useLanguage } from "../i18n/LanguageContext";
import { VolumeOff, VolumeUp } from "@mui/icons-material";

// Standalone full-page timer view at its own route (/fullscreen). Unlike
// the toggle on the timer ring, this never calls the browser's Fullscreen
// API - the page itself simply fills the viewport, which works even where
// requestFullscreen() is blocked (e.g. embedded/iframe contexts).
export default function FullscreenTimer() {
  const { language, t } = useLanguage();
  const {
    starttime,
    agenda,
    isEmpty,
    isOver,
    value,
    maxValue,
    timeLeft,
    totalDuration,
    miniAgenda,
    activeCard,
    now,
    isPaused,
  } = useTimerData(language);

  const { enabled, setEnabled, warningLeadSeconds } = useSoundSettings();
  useWakeLock(Boolean(activeCard) && !isPaused);
  useTimerSound({
    enabled,
    warningLeadSeconds,
    isEmpty,
    isOver,
    activeCard,
    starttime,
  });

  const activeTask = activeCard ? agenda[activeCard.index] : null;

  let center = (
    <div className="ring-center">
      <div className="ring-topic">{t("common.noCardsYet")}</div>
    </div>
  );

  if (starttime && agenda.length > 0) {
    center = isOver ? (
      <div className="ring-center">
        <div className="ring-topic">{t("common.done")}</div>
      </div>
    ) : (
      <div style={{ width: "84%" }}>
        <RecursiveProgressbar
          maxValue={maxValue}
          array={agenda}
          strokeWidth={8}
          starttime={starttime}
          actualTime={now}
        />
      </div>
    );
  }

  return (
    <div className="timer-widget is-fullscreen fullscreen-page">
      <div className="timer-fullscreen-btn-dock">
        <IconButton
          className="timer-fullscreen-btn"
          size="small"
          aria-label={enabled ? t("timer.mute") : t("timer.unmute")}
          onClick={() => setEnabled(!enabled)}
        >
          {enabled ? (
            <VolumeUp fontSize="small" />
          ) : (
            <VolumeOff fontSize="small" />
          )}
        </IconButton>
      </div>
      <div className="row">
        <div className="col-md-12">
          <div style={{ width: "100%", height: "100%" }}>
            <CircularProgressbarWithChildren
              value={value}
              strokeWidth={8}
              maxValue={maxValue}
              styles={buildStyles({
                pathColor: "#9A4B4F",
                strokeLinecap: "butt",
                trailColor: "rgba(46, 42, 34, 0.08)",
              })}
            >
              {center}
            </CircularProgressbarWithChildren>
          </div>
        </div>
        <div className="col-md-12 time-left">
          <span className="cap">{t("common.timeLeft")}</span>
          <span className="digits">{timeLeft}</span>
        </div>
      </div>

      <LiveControls isPaused={isPaused} isRunning={Boolean(activeCard)} />

      {activeTask && (activeTask.owner || activeTask.note) && (
        <div className="timer-fullscreen-details">
          {activeTask.owner && (
            <div className="timer-fullscreen-owner">{activeTask.owner}</div>
          )}
          {activeTask.note && (
            <div className="timer-fullscreen-note">{activeTask.note}</div>
          )}
        </div>
      )}

      {starttime && (
        <div className="timer-fullscreen-side">
          <div className="timer-fullscreen-meta">
            <span>{t("common.start", { time: formatTime(starttime) })}</span>
            {totalDuration && (
              <>
                <span className="timer-fullscreen-meta-sep">·</span>
                <span>{t("common.total", { duration: totalDuration })}</span>
              </>
            )}
          </div>

          {miniAgenda.length > 0 && (
            <div className="timer-fullscreen-agenda">
              {miniAgenda.map(({ task, status, start, end }) => (
                <div
                  key={task.id}
                  className="mini-card"
                  data-status={status}
                  data-checked={Boolean(task.done)}
                  style={{
                    transform: `rotate(${CARD_ROTATION[status]})`,
                    borderLeft: CARD_TYPES[task.type]
                      ? `3px solid ${CARD_TYPES[task.type].color}`
                      : undefined,
                  }}
                >
                  {task.done ? (
                    <span className="mini-card-check" aria-label={t("common.completed")}>
                      ✓
                    </span>
                  ) : (
                    <span
                      className="mini-card-dot"
                      style={{ background: CARD_COLOR[status] }}
                    />
                  )}
                  <span className="mini-card-topic">
                    {task.topic || t("common.untitled")}
                  </span>
                  <span className="mini-card-time">
                    {formatTime(start)}–{formatTime(end)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
